import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  DateFlight,
  FlightDetails,
  Journeys,
} from '../app/interfaces/results.interface';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MatMomentDateModule,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MY_DATE_FORMATS } from '../app/app.config';

import * as moment from 'moment';
import 'moment/locale/es';
import { TripTypeChipsComponent } from '../app/shared/trip-type-chips/trip-type-chips.component';
import { ButtonMenuComponent } from '../app/shared/button-menu/button-menu.component';
import { TripType } from '../app/utils/tripType.enums';
import { ButtonComponent } from '../app/shared/button/button.component';
import { allowOnlyDateCharacters } from '../app/utils/allow-only-date-characters';
import { AutocompleteFieldComponent } from '../app/shared/autocomplete-button/autocomplete-field.component';
import { ResultsService } from '../app/services/results.service';
import { Option, Pax } from '../app/shared/interfaces/button-menu.interface';
import { IdName } from '../app/interfaces/generic.interface';
import { listClass, listMoney } from '../app/constants/constants';
import { NgClass } from '@angular/common';
import { MultiDialogTriggerComponent } from 'app/shared/multi-dialog-trigger/multi-dialog-trigger.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'environments/environment';

moment.locale('es-AR');
@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    ButtonMenuComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMomentDateModule,
    ButtonComponent,
    AutocompleteFieldComponent,
    TripTypeChipsComponent,
    NgClass,
  ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css',
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class SearchComponent implements OnInit, OnChanges {
  @Input() searchData: DateFlight | null = null;
  @Input() id: string = 'beee505db91f4e04';
  @Output() search = new EventEmitter<DateFlight>();
  @ViewChild('mainDiv', { static: false }) mainDiv!: ElementRef<HTMLDivElement>;

  optionClass = listClass;
  optionMoney = listMoney;

  originOptions: IdName[] = [];
  filteredOriginOptions: BehaviorSubject<IdName[]>[] = [];
  isLoadingOrigin: boolean[] = [];
  isDescriptionOrigin: boolean[] = [];

  destinationOptions: IdName[] = [];
  filteredDestinationOptions: BehaviorSubject<IdName[]>[] = [];
  isLoadingDestination: boolean[] = [];
  isDescriptionDestination: boolean[] = [];

  tripTypeSelected = TripType.ROUND_TRIP;

  minDateOrigin = new Date();
  minDateDestination = new Date();

  formSearchFlight: FormGroup;

  private readonly _adapter = inject(DateAdapter);
  readonly dateFormatString = computed(() => 'DD-MM-YYYY');
  readonly dialog = inject(MatDialog);

  private fb = inject(FormBuilder);
  private resultsService = inject(ResultsService);

  pax = {
    adults: 1,
    children: 0,
    infants: 0,
  };
  selectedClass!: Option;
  selectedMoney!: Option;

  allowOnlyDateCharacters = allowOnlyDateCharacters;

  isLoading = true;
  sizeScreen = 0;
  private ro?: ResizeObserver;
  totalPax = 1;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.formSearchFlight = this.fb.group({
      flightDetails: this.fb.array([this.createFlightDetailGroup(0)]),
      pax: [this.pax],
      class: [{ label: 'Económica', value: 'Y' }],
      tripType: [TripType.ROUND_TRIP],
      money: [{ value: 'ARS', label: 'ARS' }],
    });
  }

  ngOnInit() {
    this._adapter.setLocale('es-AR');
    if (this.searchData) {
      this.selectedClass =
        this.optionClass.find(
          (option: any) => option.value === this.searchData?.cabinKind
        ) || this.optionClass[1];
      this.selectedMoney =
        this.optionMoney.find(
          (option: any) => option.value === this.searchData?.currency
        ) || this.optionMoney[0];
      this.tripTypeSelected = this.searchData.searchKind;
      this.onTripTypeSelected(this.tripTypeSelected);
      this.populateOpenJawJourneys(this.searchData.journeys);
      this.pax = { adults: 0, children: 0, infants: 0 };
      this.searchData.passengersAges.forEach((age: string) => {
        switch (age) {
          case 'ADT':
            this.pax.adults++;
            break;
          case 'CHD':
            this.pax.children++;
            break;
          case 'INF':
            this.pax.infants++;
            break;
        }
      });
      this.formSearchFlight.patchValue({
        pax: this.pax,
        class: this.selectedClass,
        money: this.selectedMoney,
      });
    }
    this.flightDetails?.controls.forEach((group, index) => {
      this.initFlightDetailGroupSubscriptions(group as FormGroup, index);
    });
    setTimeout(() => {
      this.isLoading = false;
    }, 2000);
  }

  private updateSizeFromContainer(): void {
    const rect = (
      this.elRef?.nativeElement ?? this.mainDiv.nativeElement
    ).getBoundingClientRect();
    const width = Math.round(rect.width);
    if (width && width !== this.sizeScreen) {
      this.sizeScreen = width;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
  }
  ngAfterViewInit(): void {
    const host = this.elRef.nativeElement;
    const shadowRoot = (host as any).shadowRoot as ShadowRoot | null;

    if (shadowRoot) {
      const links = [
        {
          href: 'https://fonts.googleapis.com/icon?family=Material+Icons',
          rel: 'stylesheet',
        },
        {
          href: 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined',
          rel: 'stylesheet',
        },
        {
          href: environment.stylesUrl,
          rel: 'stylesheet',
        },
      ];

      links.forEach((linkInfo) => {
        if (!shadowRoot.querySelector(`link[href="${linkInfo.href}"]`)) {
          const link = document.createElement('link');
          link.href = linkInfo.href;
          link.rel = linkInfo.rel;
          shadowRoot.appendChild(link);
        }
      });
    }

    this.updateSizeFromContainer();
    this.ngZone.runOutsideAngular(() => {
      this.ro = new ResizeObserver(() => {
        this.ngZone.run(() => {
          this.updateSizeFromContainer();
        });
      });
      this.ro.observe(this.elRef.nativeElement);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchData']) {
      this.selectedMoney =
        this.optionMoney.find(
          (option: any) =>
            option.value === changes['searchData']?.currentValue?.currency
        ) || this.optionMoney[0];
      this.formSearchFlight.patchValue({
        money: this.selectedMoney,
      });
    }
  }

  populateOpenJawJourneys(journeys: Journeys[]) {
    const flightDetailsArray = this.flightDetails;
    flightDetailsArray.clear();
    switch (this.tripTypeSelected) {
      case TripType.ROUND_TRIP:
        const group = this.createFlightDetailGroup(0);
        group.patchValue({
          origin: {
            airportName: journeys[0].nameOrigin?.name || '',
            city: journeys[0].nameOrigin?.city || '',
            country: journeys[0].nameOrigin?.country || '',
            name: `${journeys[0].origin || ''} ${
              journeys[0].nameOrigin?.name || ''
            }, ${journeys[0].nameOrigin?.city || ''}, ${
              journeys[0].nameOrigin?.country || ''
            }`,
          },
          destination: {
            airportName: journeys[0]?.nameDestine?.name || '',
            city: journeys[0]?.nameDestine?.city || '',
            country: journeys[0]?.nameDestine?.country || '',
            name: `${journeys[0]?.destination || ''} ${
              journeys[0]?.nameDestine?.name || ''
            }, ${journeys[0]?.nameDestine?.city || ''}, ${
              journeys[0]?.nameDestine?.country || ''
            }`,
          },
          originDate: journeys[0].date,
          destinationDate: journeys[1]?.date || '',
        });
        flightDetailsArray.push(group);
        break;
      case TripType.ONE_WAY:
        const oneWayGroup = this.createFlightDetailGroup(0);
        oneWayGroup.patchValue({
          origin: {
            airportName: journeys[0].nameOrigin?.name || '',
            city: journeys[0].nameOrigin?.city || '',
            country: journeys[0].nameOrigin?.country || '',
            name: `${journeys[0].origin || ''} ${
              journeys[0].nameOrigin?.name || ''
            }, ${journeys[0].nameOrigin?.city || ''}, ${
              journeys[0].nameOrigin?.country || ''
            }`,
          },
          destination: {
            airportName: journeys[0]?.nameDestine?.name || '',
            city: journeys[0]?.nameDestine?.city || '',
            country: journeys[0]?.nameDestine?.country || '',
            name: `${journeys[0]?.destination || ''} ${
              journeys[0]?.nameDestine?.name || ''
            }, ${journeys[0]?.nameDestine?.city || ''}, ${
              journeys[0]?.nameDestine?.country || ''
            }`,
          },
          originDate: journeys[0].date,
          destinationDate: '',
        });
        oneWayGroup.get('destinationDate')?.setValue('');
        oneWayGroup.get('destinationDate')?.clearValidators();
        oneWayGroup.get('destinationDate')?.disable();
        oneWayGroup.get('destinationDate')?.updateValueAndValidity();
        flightDetailsArray.push(oneWayGroup);
        break;
      case TripType.OPEN_JAW:
        journeys.forEach((journey, index) => {
          const group = this.createFlightDetailGroup(index);
          group.patchValue({
            origin: {
              airportName: journey.nameOrigin?.name || '',
              city: journey.nameOrigin?.city || '',
              country: journey.nameOrigin?.country || '',
              name: `${journey.origin || ''} ${
                journey.nameOrigin?.name || ''
              }, ${journey.nameOrigin?.city || ''}, ${
                journey.nameOrigin?.country || ''
              }`,
            },
            destination: {
              airportName: journey.nameDestine?.name || '',
              city: journey.nameDestine?.city || '',
              country: journey.nameDestine?.country || '',
              name: `${journey.destination || ''} ${
                journey.nameDestine?.name || ''
              }, ${journey.nameDestine?.city || ''}, ${
                journey.nameDestine?.country || ''
              }`,
            },
            originDate: journey.date,
            destinationDate: '',
          });

          group.get('destinationDate')?.setValue('');
          group.get('destinationDate')?.clearValidators();
          group.get('destinationDate')?.disable();
          group.get('destinationDate')?.updateValueAndValidity();

          flightDetailsArray.push(group);
        });
        break;
    }
  }
  private getOriginDate(index: number): Date | null {
    const g = this.flightDetails.at(index) as FormGroup;
    const raw = g.get('originDate')?.value;
    return this.normalizeToDate(raw);
  }
  private normalizeToDate(value: any): Date | null {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  private setOriginDate(index: number, date: Date): void {
    const g = this.flightDetails.at(index) as FormGroup;
    g.get('originDate')?.setValue(new Date(date), { emitEvent: false });
  }
  private initFlightDetailGroupSubscriptions(group: FormGroup, index: number) {
    this.filteredOriginOptions[index] = new BehaviorSubject<IdName[]>([]);
    this.isLoadingOrigin[index] = false;

    group
      .get('origin')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value: string) => {
          this.isLoadingOrigin[index] = value.length >= 3;
          if (value.length <= 2) {
            this.filteredOriginOptions[index].next([]);
            this.isLoadingOrigin[index] = false;
          }
        }),
        filter((value: string) => value.length >= 3),
        switchMap((value: string) => this.getAirport(value))
      )
      .subscribe((results) => {
        this.isLoadingOrigin[index] = false;
        if (results.length === 0) {
          this.filteredOriginOptions[index].next([
            { id: '1', name: 'No hay resultados' },
          ]);
        } else {
          this.filteredOriginOptions[index].next(results);
        }
      });

    this.filteredDestinationOptions[index] = new BehaviorSubject<IdName[]>([]);
    this.isLoadingDestination[index] = false;

    group
      .get('destination')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value: string) => {
          this.isLoadingDestination[index] = value.length >= 3;
          if (value.length <= 2) {
            this.filteredDestinationOptions[index].next([]);
            this.isLoadingDestination[index] = false;
          }
        }),
        filter((value: string) => value.length >= 3),
        switchMap((value: string) => this.getAirport(value))
      )
      .subscribe((results) => {
        this.isLoadingDestination[index] = false;
        if (results.length === 0) {
          this.filteredDestinationOptions[index].next([
            { id: '1', name: 'No hay resultados' },
          ]);
        } else {
          this.filteredDestinationOptions[index].next(results);
        }
      });

    group.get('originDate')?.valueChanges.subscribe((newOriginDate) => {
      if (this.tripTypeSelected === TripType.OPEN_JAW && newOriginDate) {
        const newDate = this.normalizeToDate(newOriginDate);
        for (let j = index - 1; j >= 0; j--) {
          const prev = this.getOriginDate(j);
          if (prev && newDate && prev.getTime() > newDate.getTime()) {
            this.setOriginDate(j, newDate);
          }
        }
        for (let j = index + 1; j < this.flightDetails.length; j++) {
          const next = this.getOriginDate(j);
          if (next && newDate && next.getTime() < newDate.getTime()) {
            this.setOriginDate(j, newDate);
          }
        }
        group.get('destinationDate')?.setValue('');
      }

      if (this.tripTypeSelected === TripType.ROUND_TRIP) {
        this.minDateDestination = new Date(newOriginDate);
        group.get('destinationDate')?.setValue('', { emitEvent: false });
      }
    });

    group
      .get('destinationDate')
      ?.valueChanges.subscribe((newDestinationDate) => {
        if (this.tripTypeSelected === TripType.ROUND_TRIP) {
          const originDate = group.get('originDate')?.value;

          if (originDate && newDestinationDate) {
            const origin = new Date(originDate);
            const destination = new Date(newDestinationDate);

            if (destination < origin) {
              group
                .get('destinationDate')
                ?.setValue(originDate, { emitEvent: false });
            }
          }
        }
      });
  }

  getAirport(query: string): Observable<IdName[]> {
    return this.resultsService.getAirport(query);
  }

  getStyledInputText(value: string = ''): string {
    if (!value) return '';
    const bold = value.slice(0, 3);
    const rest = value.slice(3);
    return `<b>${bold}</b>${rest}`;
  }

  createFlightDetailGroup(index: number): FormGroup {
    const group = this.fb.group({
      origin: ['', [Validators.required]],
      destination: ['', [Validators.required]],
      originDate: ['', [Validators.required]],
      destinationDate: ['', [Validators.required]],
    });

    this.initFlightDetailGroupSubscriptions(group, index);

    return group;
  }

  get flightDetails(): FormArray {
    return this.formSearchFlight.get('flightDetails') as FormArray<FormGroup>;
  }

  addFlightDetail() {
    const newIndex = this.flightDetails.length;
    const newFlightDetail = this.createFlightDetailGroup(newIndex);

    if (this.tripTypeSelected !== TripType.ROUND_TRIP) {
      newFlightDetail.get('destinationDate')?.disable();
      newFlightDetail.get('destinationDate')?.clearValidators();
    }

    this.flightDetails.push(newFlightDetail);
  }

  displayFn(value: string | IdName): string {
    if (typeof value === 'string') return value;
    return value?.name ?? '';
  }

  highlightMatch(option: string, filter: string): string {
    if (!filter || typeof filter !== 'string') {
      return option;
    }
    const escapedFilter = filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedFilter.toLowerCase()})`, 'gi');
    return option.replace(regex, '<span class="highlight">$1</span>');
  }

  get flightDetailsGroups(): FormGroup[] {
    return this.flightDetails.controls as FormGroup[];
  }

  removeFlightDetail(index: number) {
    const updatedFlightDetails = this.flightDetails.controls
      .filter((_, i) => i !== index)
      .map((control) => {
        const destinationDateControl = this.fb.control(
          control.get('destinationDate')?.value
        );

        if (this.tripTypeSelected === TripType.ROUND_TRIP) {
          destinationDateControl.setValidators(Validators.required);
          destinationDateControl.enable();
        } else {
          destinationDateControl.clearValidators();
          destinationDateControl.disable();
        }

        return this.fb.group({
          origin: [control.get('origin')?.value, Validators.required],
          destination: [control.get('destination')?.value, Validators.required],
          originDate: [control.get('originDate')?.value, Validators.required],
          destinationDate: destinationDateControl,
        });
      });

    this.formSearchFlight.setControl(
      'flightDetails',
      this.fb.array(updatedFlightDetails)
    );

    this.onTripTypeSelected(this.tripTypeSelected, true);
  }

  onTripTypeSelected(id: TripType, isDelete?: boolean): void {
    this.tripTypeSelected = id;

    this.formSearchFlight.patchValue({ tripType: id });

    if (id === TripType.ROUND_TRIP || id === TripType.ONE_WAY) {
      if (this.flightDetails.length > 1) {
        const firstFlightDetailValue = this.flightDetails.at(0).value;

        const newFormArray = this.fb.array([
          this.fb.group({
            origin: [firstFlightDetailValue.origin, Validators.required],
            destination: [
              firstFlightDetailValue.destination,
              Validators.required,
            ],
            originDate: [
              firstFlightDetailValue.originDate,
              Validators.required,
            ],
            destinationDate: [
              firstFlightDetailValue.destinationDate,
              id === TripType.ROUND_TRIP ? Validators.required : [],
            ],
          }),
        ]);
        this.formSearchFlight.setControl('flightDetails', newFormArray);
        this.initFlightDetailGroupSubscriptions(
          newFormArray.at(0) as FormGroup,
          0
        );
      }
    }

    this.flightDetails.controls.forEach((group: AbstractControl) => {
      const destinationDateControl = group.get('destinationDate');

      if (!destinationDateControl) return;

      switch (id) {
        case TripType.ROUND_TRIP:
          destinationDateControl.enable();
          destinationDateControl.setValidators(Validators.required);
          break;
        case TripType.ONE_WAY:
          destinationDateControl.reset();
          destinationDateControl.clearValidators();
          destinationDateControl.disable();
          break;
        case TripType.OPEN_JAW:
          destinationDateControl.reset();
          destinationDateControl.clearValidators();
          destinationDateControl.disable();
          if (!isDelete) this.addFlightDetail();
          break;
      }
      destinationDateControl.updateValueAndValidity();
    });
  }

  updateForm(value: Option | Pax, type: string): void {
    this.formSearchFlight.patchValue({
      [type]: value,
    });
  }
  searchFlight() {
    this.resultsService.getURL(this.id).subscribe({
      next: (response) => {
        const {
          pax,
          flightDetails,
          money,
          class: cabinClass,
          tripType,
        } = this.formSearchFlight.value;

        const passengersAges = [
          String(pax.adults),
          String(pax.children),
          String(pax.infants),
        ];

        const codes: string[] = [];
        const dates: string[] = [];

        flightDetails.forEach((flight: FlightDetails) => {
          const getCode = (place: IdName) =>
            place?.name?.slice(0, 3).toUpperCase() || '';
          const formatDate = (date: string) => {
            const d = new Date(date);
            if (isNaN(d.getTime())) return '';

            const day = String(d.getUTCDate()).padStart(2, '0');
            const month = String(d.getUTCMonth() + 1).padStart(2, '0');
            const year = d.getUTCFullYear();

            return `${day}-${month}-${year}`;
          };

          const originCode = getCode(flight.origin);
          const destCode = getCode(flight.destination);

          codes.push(`${originCode}-${destCode}`);
          dates.push(formatDate(flight.originDate));
          if (flight.destinationDate) {
            dates.push(formatDate(flight.destinationDate));
          }
        });
        const urlParams = [
          money.value,
          tripType,
          codes.join(','),
          dates.join(','),
          passengersAges.join(','),
          cabinClass.value,
          this.id,
        ].join('/');
        window.open(`${response.url}/results?${urlParams}`);
      },
    });
  }
  openDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string,
    type: 'money' | 'pax' | 'class'
  ): void {
    const data: {
      type: string;
      pax?: Pax;
      optionClass?: Option[];
      optionMoney?: Option[];
    } = { type };

    if (type === 'pax') {
      data.pax = this.formSearchFlight.get('pax')?.value;
    }

    if (type === 'class') {
      data.optionClass = listClass;
    }

    if (type === 'money') {
      data.optionMoney = listMoney;
    }

    const dialogRef = this.dialog.open(MultiDialogTriggerComponent, {
      width: '300px',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        type,
        pax: this.formSearchFlight.get('pax')?.value,
        class: this.formSearchFlight.get('class')?.value,
        money: this.formSearchFlight.get('money')?.value,
      },
      autoFocus: true,
      restoreFocus: true,
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        switch (type) {
          case 'money':
            this.updateForm(result, 'money');
            break;
          case 'pax':
            this.updateForm(result, 'pax');
            this.totalPax = result.adults + result.children + result.infants;
            break;
          case 'class':
            this.updateForm(result, 'class');
            break;
        }
      }
    });
  }
}
